/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material"
import { collection, addDoc, deleteDoc, doc, updateDoc, onSnapshot } from "firebase/firestore"
import { toast } from 'react-toastify'
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { IBusinessHoliday } from "../../utils/businessHolidays"

type Mode = 'single' | 'range'

const emptyHoliday: IBusinessHoliday = {
  startDate: '',
  endDate: '',
  reason: ''
}

const BusinessHolidays = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const holidaysRef = collection(db, 'businessHolidays')
  const [items, setItems] = useState<IBusinessHoliday[]>([])
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [mode, setMode] = useState<Mode>('single')
  const [current, setCurrent] = useState<IBusinessHoliday>(emptyHoliday)
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({})

  useEffect(() => {
    const unsubscribe = onSnapshot(holidaysRef, ({ docs }) => {
      setItems(docs.map((d) => ({ id: d.id, ...d.data() } as IBusinessHoliday)))
    })
    return unsubscribe
  }, [])

  const formatPeriod = (h: IBusinessHoliday) => {
    if (h.startDate === h.endDate) return dayjs(h.startDate).format("YYYY年MM月DD日")
    return `${dayjs(h.startDate).format("YYYY年MM月DD日")} 〜 ${dayjs(h.endDate).format("YYYY年MM月DD日")}`
  }

  const columns = [
    {
      field: "period",
      headerName: "休業期間",
      flex: 1.5,
      valueGetter: (params: any) => formatPeriod(params.row),
    },
    {
      field: "reason",
      headerName: "理由",
      flex: 1.5,
    },
    {
      field: "updatedAt",
      headerName: "更新日時",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "操作",
      flex: 0.8,
      renderCell: (params: any) => (
        <Box>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} size="small">
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => handleDelete(params.row.id)} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    }
  ]

  const rows = [...items]
    .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
    .map((item, i) => ({ No: i + 1, ...item }))

  const handleEdit = (holiday: IBusinessHoliday) => {
    setEditMode(true)
    setMode(holiday.startDate === holiday.endDate ? 'single' : 'range')
    setCurrent({
      id: holiday.id,
      startDate: holiday.startDate,
      endDate: holiday.endDate,
      reason: holiday.reason || ''
    })
    setFormErrors({})
    setOpenDialog(true)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("この休業日を削除しますか？")) return
    try {
      await deleteDoc(doc(db, "businessHolidays", id))
      toast.success("休業日を削除しました")
    } catch (e) {
      toast.error("削除に失敗しました: " + JSON.stringify(e))
    }
  }

  const handleModeChange = (_: unknown, newMode: Mode | null) => {
    if (!newMode) return
    setMode(newMode)
    if (newMode === 'single') {
      setCurrent(prev => ({ ...prev, endDate: prev.startDate }))
    }
  }

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {}

    if (!current.startDate) {
      errors.startDate = mode === 'single' ? '休業日は必須です' : '開始日は必須です'
    }
    if (mode === 'range' && !current.endDate) {
      errors.endDate = '終了日は必須です'
    }
    if (current.startDate && current.endDate && current.endDate < current.startDate) {
      errors.endDate = '終了日は開始日以降にしてください'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      const timestamp = dayjs().format("YYYY-MM-DD HH:mm:ss")
      const holidayData = {
        startDate: current.startDate,
        endDate: mode === 'single' ? current.startDate : current.endDate,
        reason: (current.reason || '').trim(),
        updatedAt: timestamp
      }

      if (editMode && current.id) {
        await updateDoc(doc(db, "businessHolidays", current.id), holidayData)
        toast.success("休業日を更新しました")
      } else {
        await addDoc(holidaysRef, {
          ...holidayData,
          createdAt: timestamp
        })
        toast.success("休業日を登録しました")
      }

      handleCloseDialog()
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditMode(false)
    setMode('single')
    setCurrent(emptyHoliday)
    setFormErrors({})
  }

  const handleOpenNewDialog = () => {
    setEditMode(false)
    setMode('single')
    setCurrent(emptyHoliday)
    setFormErrors({})
    setOpenDialog(true)
  }

  return (
    <Box m={5}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <h2>休業日マスタ</h2>
          <p>休業日を登録すると、対象期間の衛生管理点検票の入力を省略できます</p>
        </Box>
        <Button variant="contained" color="primary" onClick={handleOpenNewDialog}>
          新規休業日登録
        </Button>
      </Box>

      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid rows={rows} columns={columns} getRowHeight={() => 'auto'} />
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? '休業日編集' : '新規休業日登録'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                size="small"
              >
                <ToggleButton value="single">単日</ToggleButton>
                <ToggleButton value="range">期間指定</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {mode === 'single' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="休業日 *"
                  value={current.startDate}
                  onChange={(e) => setCurrent(prev => ({ ...prev, startDate: e.target.value, endDate: e.target.value }))}
                  error={!!formErrors.startDate}
                  helperText={formErrors.startDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="開始日 *"
                    value={current.startDate}
                    onChange={(e) => setCurrent(prev => ({ ...prev, startDate: e.target.value }))}
                    error={!!formErrors.startDate}
                    helperText={formErrors.startDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="終了日 *"
                    value={current.endDate}
                    onChange={(e) => setCurrent(prev => ({ ...prev, endDate: e.target.value }))}
                    error={!!formErrors.endDate}
                    helperText={formErrors.endDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="理由"
                value={current.reason}
                onChange={(e) => setCurrent(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="例: 年末年始休業"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">キャンセル</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editMode ? '更新' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BusinessHolidays
